import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { MF, MF_KEYS, MF_VERSIONS } from "@/lib/marketingFoundations";

const Tick = ({ on, onClick, id, label }) => (
  <button type="button" onClick={onClick} aria-pressed={on} aria-label={label} data-testid={id}
    className={`h-6 w-6 shrink-0 rounded-md border-2 flex items-center justify-center transition-colors ${on ? "bg-leaf border-leaf text-white" : "border-border bg-white hover:border-pinkb"}`}>
    {on && <Check className="h-4 w-4" />}
  </button>
);

const useChecklist = () => {
  const [done, setDone] = useState(null);
  useEffect(() => { api.get("/me/foundations-checklist").then((r) => setDone(r.data.done)).catch(() => setDone([])); }, []);
  const toggle = async (key) => {
    if (!done) return;
    const prev = done;
    const next = done.includes(key) ? done.filter((k) => k !== key) : [...done, key];
    setDone(next);
    try { await api.put("/me/foundations-checklist", { done: next }); } catch (e) { setDone(prev); toast.error(errMsg(e)); }
  };
  return [done, toggle];
};

export const BasicsGuide = ({ audience, res, onOpen }) => {
  const [v, setV] = useState(audience === "hospitality" ? "hospitality" : "cpg");
  const [done, toggle] = useChecklist();
  const c = MF[v];
  const worksheet = res.find((r) => /content pillar/i.test(r.title));
  const has = (k) => (done || []).includes(`${v}:${k}`);
  const count = MF_KEYS[v].filter(has).length;
  return (
    <div data-testid="guide-basics" className="text-sm leading-relaxed space-y-3">
      <div className="flex flex-wrap items-center gap-2" role="tablist" data-testid="mf-version-toggle">
        {MF_VERSIONS.map(([k, label]) => (
          <button key={k} role="tab" aria-selected={v === k} onClick={() => setV(k)} data-testid={`mf-version-${k}`}
            className={`rounded-full border-2 px-4 py-1.5 text-xs transition-colors ${v === k ? "border-pinkb bg-pinkb/5 text-navy" : "border-border text-muted-foreground hover:border-pinkb/50"}`}>{label}</button>
        ))}
        <span className="ml-auto font-mono text-xs text-navy" data-testid="mf-progress">{count}/{MF_KEYS[v].length} in place</span>
      </div>
      <p className="pt-2">{c.basicsIntro}</p>
      <ul className="space-y-2">{c.basics.map(([k, h, b, link]) => (
        <li key={k} className={`flex items-start gap-3 rounded-xl border p-4 transition-colors ${has(`basic-${k}`) ? "border-leaf/50 bg-leaf/5" : "border-border bg-white"}`} data-testid={`mf-basic-${k}`}>
          <Tick on={has(`basic-${k}`)} onClick={() => toggle(`${v}:basic-${k}`)} id={`mf-tick-basic-${k}`} label={`Mark "${h}" as in place`} />
          <p><b className="text-navy">{h}</b> {b}{link && <> {worksheet
            ? <button type="button" onClick={() => onOpen(worksheet)} data-testid={`mf-basic-${k}-template`} className="font-medium text-pinkb hover:underline">{link}</button>
            : <span className="text-xs font-mono uppercase text-muted-foreground" data-testid={`mf-basic-${k}-template`}>Worksheet coming soon</span>}</>}</p>
        </li>
      ))}</ul>
    </div>
  );
};
